<template>
  <div>
    <BannerLogo />
    <v-container>
      <v-row justify="center" class="mb-12">
        <v-col cols="12">
          <h4 class="text-h5 primary--text mb-4 text-center">
            For more information about HeathView Capital Partners, please contact us
          </h4>
        </v-col>
        <v-col v-if="form_submitted" cols="12" md="8" lg="6">
          <v-alert
            :value="true"
            type="success"
          >
            Thank you. Your message has been sent. Someone from our team will be in touch soon.
          </v-alert>
        </v-col>
        <v-col v-else cols="12" md="8" lg="6" xl="5">
          <v-form
            ref="form"
            v-model="formValid"
          >
            <v-card>
              <v-card-text>
                <span class="text-caption font-italic primary--text">
                  All fields required.
                </span>
                <v-text-field
                  v-model="form.name"
                  label="Name"
                  :rules="requiredRule"
                  required
                />
                <v-text-field
                  v-model="form.email"
                  label="Email"
                  :rules="emailRules"
                  required
                />
                <v-text-field
                  v-model="form.phone"
                  label="Phone"
                  :rules="phoneRules"
                  required
                />
                <v-textarea
                  v-model="form.message"
                  label="Message"
                  :rules="requiredRule"
                />
                <v-card
                  v-if="authErrorMessage"
                  flat
                  class="error--text my-4"
                  v-text="authErrorMessage"
                />
                <VueRecaptcha
                  :loadRecaptchaScript="true"
                  ref="invisibleRecaptcha"
                  size="invisible"
                  :sitekey="sitekey"
                  @verify="onVerify"
                  @expired="onExpired"
                >
                  <v-btn v-if="!verifying_captcha" :disabled="buttonEnabled < 2" class="secondary" @click="verifying_captcha = true">
                      Submit
                  </v-btn>
                  <v-btn v-else :disabled="verifying_captcha" class="secondary">
                      <v-icon class="fa-spin" left>autorenew</v-icon>
                      Submitting
                  </v-btn>
                </VueRecaptcha>
                <v-alert type="error" color="warning lighten-3" class="grey--text text--darken-4" dismissible="" :value="recaptcha_fail">
                  <span class="subheading d-block font-weight-bold">Sorry. Your message was not sent.</span>
                  You are probably not a spam bot, so please try to <strong>SUBMIT</strong> again.
                </v-alert>
              </v-card-text>
            </v-card>
          </v-form>
        </v-col>
        <v-col cols="12" md="3" lg="2">
          <section>
            <h2 class="text-h6 primary--text">
              HealthView Capital Partners
            </h2>
            <span class="d-block">
              P. O. Box 8427
            </span>
            <span class="d-block">
              Rocky Mount, NC 27804
            </span>
          </section>
          <section class="mt-4">
            <h4 class="subheading primary--text">
              Sonya Rozier
            </h4>
            <span class="d-block">
              <a href="tel:+19104744381" class="no_underline black--text">
                (910) 474-4381
              </a>
            </span>
          </section>
          <section class="mt-4">
            <h4 class="subheading primary--text">
              Sandra Lindsey
            </h4>
            <span class="d-block">
              <a href="tel:+19192012178" class="no_underline black--text">
                (919) 201-2178
              </a>
            </span>
          </section>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import BannerLogo from '@/components/core/BannerLogo'
import VueRecaptcha from 'vue-recaptcha'
import { db } from '@/firebase'
import axios from 'axios'

const requiredRule = [v => !!v || 'Field is required']
const phoneRules = requiredRule.concat([
  v =>
    /^((1)?[\s-]?)?\(?[2-9]\d\d\)?[\s-]?[2-9]\d\d[\s-]?\d\d\d\d/.test(v) ||
    'Phone must be valid',
])
const emailRules = requiredRule.concat([
  v => /.+@.+/.test(v) || 'E-mail must be valid',
])

export default {
  name: 'Contact',
  metaInfo: {
    title: 'Contact',
    meta: [
      {description: 'If you’re seeking a partner with strong experience in sales/marketing and operational expertise, as well as capital, let’s talk.'}
    ]
  },
  components: {
    BannerLogo,
    VueRecaptcha,
  },
  data() {
    return {
      requiredRule,
      phoneRules,
      emailRules,
      sitekey: process.env.VUE_APP_RECAPTCHA_KEY,
      title: 'Contact',
      formValid: false,
      authErrorMessage: '',
      form: {
        name: '',
        email: '',
        phone: '',
        message: '',
      },
      form_submitted: false,
      verifying_captcha: false,
      recaptcha_fail: false
    }
  },
  computed: {
    buttonEnabled() {
      return this.formValid + !this.formEmptyFields
    },
    formEmptyFields() {
      const f = this.form
      const vals = Object.values(f)
      return !vals.every(val => val !== '')
    },
  },
  methods: {
    async submitForm(token) {
      const ref = db.ref('/hv_cp_contact_form')
      const form = this.form
      form.token = token
      form.simple_date = new Date().toLocaleString()
      try {
        await ref.push(form)
      } catch (e) {
        // TODO: error handling
        console.error(e)
      }
      this.form_submitted = true
    },
    onVerify(response) {
      const url =
        'https://us-central1-healthview-homehealth-c89m.cloudfunctions.net/checkRecaptcha?response=' +
        encodeURIComponent(response)
      const headers = {
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      }
      if (response) {
        axios.post(url, response, headers).then(res => {
          if (res.data === 'Recaptcha verification failed. Are you a robot?') {
            this.$refs.invisibleRecaptcha.reset()
            this.form_submitted = false
            this.verifying_captcha = false
          } else if (res.data === true) {
            this.submitForm(response)
          } else {
            this.$refs.invisibleRecaptcha.reset()
            this.form_submitted = false
            this.verifying_captcha = false
          }
        })
      }
    },
    onExpired() {
      console.log('Expired')
    },
    resetRecaptcha() {
      this.$refs.invisibleRecaptcha.reset() // Direct call reset method
    },
  },
  mounted() {
    this.form_submitted = false
    this.$refs.invisibleRecaptcha.reset() // Direct call reset method
  }
}
</script>

<style>
.rc-anchor{
  z-index: 10 !important;
}
a.no_underline {
  text-decoration: none;
}

.fa-spin {
  -webkit-animation: fa-spin 2s infinite linear;
  animation: fa-spin 2s infinite linear;
}
.fa-pulse {
  -webkit-animation: fa-spin 1s infinite steps(8);
  animation: fa-spin 1s infinite steps(8);
}
@-webkit-keyframes fa-spin {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
@keyframes fa-spin {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
</style>
